import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'next-i18next';

import styles from './index-detail-row.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { Flag } from '@utmb/design-system/flag/Flag';
import { Link } from '@utmb/design-system/link/link';
import { Icon } from '@utmb/design-system/icons/Icon';
import { HelpButton } from '@utmb/design-system/help-button/HelpButton';
import { ScoreLegend } from '@utmb/design-system/index/score-legend/score-legend';
import { IconsEnum } from '@utmb/types/icons';
import { DateIndexDetail } from '../date-index-detail/date-index-detail';
import { ScoreDetail } from '../score-detail/score-detail';
import { ColorTypesEnum } from '@utmb/types/colors';
import { getColorStyle } from '@utmb/utils/colors';
import { IndexBreakdownRow } from '@utmb/utils/index-calculation';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';

export interface IndexDetailRowProps {
    row: IndexBreakdownRow;
    /** 1-based rank of the row (displayed as "01", "02", …). */
    position: number;
    /** Best adjusted score, reference used to compute each degraded best score. */
    bestScore: number;
    isAuthenticated: boolean;
    login: () => void;
}

export function IndexDetailRow({ row, position, bestScore, isAuthenticated, login }: IndexDetailRowProps): ReactElement {
    const { t } = useTranslation(['common', 'myutmb']);

    // Below xl the row collapses to a summary (race + retained score); the detail
    // band is toggled open/closed here.
    const [isExpanded, setIsExpanded] = useState(false);

    // Below xl, the "Score garanti" label in the band is tappable and reveals an
    // explanatory popover (the desktop hover tooltip lives in the popin header).
    const [dbsHelpOpen, setDbsHelpOpen] = useState(false);
    // The popover is position: absolute against .table (the rows' positioned
    // container), so it escapes the cards' overflow: hidden yet still scrolls with the
    // content. Its placement is computed from the row's offset within .table, anchoring
    // it just below the clicked row.
    const rowRef = useRef<HTMLDivElement>(null);
    const [helpPos, setHelpPos] = useState<{ top: number; left: number; width: number } | null>(null);

    const openDbsHelp = (): void => {
        const el = rowRef.current;
        if (el) setHelpPos({ top: el.offsetTop + el.offsetHeight + 8, left: el.offsetLeft, width: el.offsetWidth });
        setDbsHelpOpen(true);
    };

    return (
        <div
            ref={rowRef}
            className={`${styles.row} ${isAuthenticated ? '' : styles.notAuthenticated} ${isExpanded ? styles.expanded : ''} ${
                dbsHelpOpen ? styles.helpOpen : ''
            }`}
        >
            {/* 5 Meilleures performances */}
            <div className={styles.race} data-label={t('singleRunner.statistics.indexDetail.fiveBestPerf')}>
                <Font.FuturaMedium className={styles.position} as="span" desktop="24" mobile="24">
                    {String(position).padStart(2, '0')}
                </Font.FuturaMedium>
                <div className={styles.positionDivider} />
                {row.result ? (
                    <Link className={styles.raceLink} href={`/utmb-index/races/${row.result.uri}`} prefetch={false}>
                        <div className={styles.wrapCategoryName}>
                            <PiCategoryLogo height={12} piCategory={row.result.piCategory} isDark={false} />
                            <Font.FuturaMedium className={styles.name} desktop="18" mobile="14">
                                {row.result.raceName}
                            </Font.FuturaMedium>
                        </div>
                        <div className={styles.wrapCountryEvent}>
                            <Flag isoCode={row.result.countryCode} className={styles.flag} hideShadow />
                            <Font.FuturaBook className={styles.event} desktop="16" mobile="12" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                                {row.result.eventName} {row.result.eventYear}
                            </Font.FuturaBook>
                        </div>
                    </Link>
                ) : (
                    <Font.FuturaBook className={styles.fillerLabel} desktop="16" mobile="14">
                        {t('singleRunner.statistics.indexDetail.guaranteedScore')}
                    </Font.FuturaBook>
                )}
            </div>

            {/* Detail columns. Wrapped so that below xl they animate open/closed as one
                collapsible band; on desktop the wrapper is transparent (display: contents)
                and each cell stays its own grid column. */}
            <div className={styles.details}>
                {/* Score obtenu */}
                <div className={styles.baseScore} data-label={t('singleRunner.statistics.indexDetail.scoreObtained')}>
                    {!isAuthenticated ? (
                        <button type="button" className={styles.loginButton} onClick={login}>
                            <Font.OxaniumRegular desktop="14" mobile="12">
                                {t('singleRunner.resultsTable.connectSeeIndex')}
                            </Font.OxaniumRegular>
                        </button>
                    ) : row.result ? (
                        row.baseScore > VALID_INDEX_THRESHOLD ? (
                            <Font.OxaniumMedium className={styles.baseScoreValue} desktop="36" mobile="36">
                                {row.baseScore}
                            </Font.OxaniumMedium>
                        ) : (
                            <div className={styles.validIndexWrap}>
                                <Font.OxaniumMedium className={styles.validIndex} desktop="20" mobile="14">
                                    {t('myutmb:myUtmb.myStones.reason.race')}
                                </Font.OxaniumMedium>
                                <HelpButton ariaLabel="Aide" className={styles.validIndexHelpButton}>
                                    <ScoreLegend />
                                </HelpButton>
                            </div>
                        )
                    ) : null}
                </div>

                {/* Date */}
                <div className={styles.date} data-label={t('singleRunner.statistics.indexDetail.date')}>
                    {row.result && <DateIndexDetail dateIso={row.result.dateIso} monthsAge={row.monthsAge} degradationPct={row.ageDegradationPct} />}
                </div>

                {isAuthenticated && (
                    <>
                        {/* Score ajusté */}
                        <div className={styles.adjusted} data-label={t('singleRunner.statistics.indexDetail.adjustedScore')}>
                            {row.result && row.adjustedScore > VALID_INDEX_THRESHOLD ? (
                                <ScoreDetail referenceScore={row.baseScore} score={row.adjustedScore} isDisabled={row.isDbsRetained} />
                            ) : null}
                        </div>

                        {/* Score garanti (Degraded Best Score) */}
                        <div className={styles.dbs} data-label={t('singleRunner.statistics.indexDetail.guaranteedScore')}>
                            <button
                                type="button"
                                className={styles.dbsLabel}
                                onClick={() => (dbsHelpOpen ? setDbsHelpOpen(false) : openDbsHelp())}
                                aria-expanded={dbsHelpOpen}
                            >
                                {t('singleRunner.statistics.indexDetail.guaranteedScore')}
                            </button>
                            {row.degradedBestScore > VALID_INDEX_THRESHOLD ? (
                                <ScoreDetail referenceScore={bestScore} score={row.degradedBestScore} isDisabled={!row.isDbsRetained} />
                            ) : null}
                            {dbsHelpOpen && (
                                <div
                                    className={styles.dbsHelp}
                                    role="tooltip"
                                    style={helpPos ? { top: helpPos.top, left: helpPos.left, width: helpPos.width } : undefined}
                                >
                                    <button
                                        type="button"
                                        className={styles.dbsHelpClose}
                                        onClick={() => setDbsHelpOpen(false)}
                                        aria-label={t('close')}
                                    >
                                        <Icon variant={IconsEnum.CLOSE} width={16} height={16} />
                                    </button>
                                    <Font.FuturaMedium as="span" desktop="16" mobile="14">
                                        {t('singleRunner.statistics.indexDetail.guaranteedScore')}
                                    </Font.FuturaMedium>
                                    <Font.FuturaBook as="span" desktop="14" mobile="12">
                                        {t('singleRunner.statistics.indexDetail.guaranteedScoreExplanation')}
                                    </Font.FuturaBook>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Retained score + toggle. Grouped so they sit together on the right of the
                compact summary line; on desktop the wrapper is transparent and the toggle
                is hidden, so retained stays its own grid column. */}
            {isAuthenticated && (
                <div className={styles.summaryRight}>
                    <div className={styles.retained} data-label={t('singleRunner.statistics.indexDetail.retained')}>
                        {row.retainedScore > VALID_INDEX_THRESHOLD ? (
                            <Font.OxaniumBold as="span" className={styles.retainedValue} desktop="36" mobile="36" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                                {Math.round(row.retainedScore)}
                            </Font.OxaniumBold>
                        ) : null}
                    </div>

                    {/* Compact-only toggle (below xl): ••• to open, × to close. */}
                    <button
                        type="button"
                        className={styles.toggle}
                        onClick={() =>
                            setIsExpanded((prev) => {
                                if (prev) setDbsHelpOpen(false);
                                return !prev;
                            })
                        }
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Masquer le détail' : 'Afficher le détail'}
                    >
                        {isExpanded ? <Icon variant={IconsEnum.CLOSE} width={16} height={16} /> : <span className={styles.dots} />}
                    </button>
                </div>
            )}
        </div>
    );
}
